import { useMemo } from "react";
import useSWR, { BareFetcher, SWRConfiguration, SWRResponse } from "swr";
import { hydrateAuthenticationUser } from "~/helpers/api/authentication";
import useUserAuthentication from "~/hooks/contexts/useUserAuthentication";
import { SwrError } from "~/lib/swr";

type Config = SWRConfiguration<Data, SwrError, BareFetcher<Data>>;
type Data = AuthenticationUser | null | undefined;
type HydratedData = HydratedAuthenticationUser | null | undefined;
type Response = Overwrite<SWRResponse<Data, SwrError>, {
  data: HydratedData | undefined;
}>;

export default function useAuthenticationUser(config?: Config): Response {
  const { userAccessToken } = useUserAuthentication();

  const swrConfig = useMemo<Config>(() => {
    const v = {
      ...config,
    };

    if (userAccessToken === undefined) {
      v.fetcher = () => undefined;
    } else if (userAccessToken === null || (userAccessToken.isMfaRequired && !userAccessToken.isMfaComplete)) {
      v.fetcher = () => null;
    }

    return v;
  }, [config, userAccessToken]);

  const { data: rawData, ...swrResponse } = useSWR<Data, SwrError>("/authentication/me", swrConfig);

  const data = useMemo<Response["data"]>(() => {
    if (rawData === undefined || rawData === null) {
      return rawData;
    }

    return hydrateAuthenticationUser(rawData);
  }, [rawData]);

  return { data, ...swrResponse };
}
